import { CssBaseline, ThemeProvider } from "@mui/material";
import React, { useContext, useEffect, useMemo } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import "./App.css";
import AccountMaintenance from "./screens/Commercial Airline/Account Maintenance/AccountMaintenance";
import CommercialAirlineHome from "./screens/Commercial Airline/CommercialAirlineHome";
import ConcessionFee from "./screens/Commercial Airline/Concession Fee/ConcesssionFee";
import AirlineList from "./screens/Commercial Airline/Data Source/AirlineList";
import DataSource from "./screens/Commercial Airline/Data Source/DataSource";
import AviationStackFailure from "./screens/Commercial Airline/Data Source/Views/AviationStackFailure";
import AviationStackSuccess from "./screens/Commercial Airline/Data Source/Views/AviationStackSuccess";
import DisputeDetailsScreen from "./screens/Commercial Airline/Disputes/DisputeDetailsScreen";
import CreateFlightPlan from "./screens/Commercial Airline/Flight Roster/Create Flight Plan/CreateFlightPlan";
import EditFlightRoster from "./screens/Commercial Airline/Flight Roster/Edit Flight Roster/EditFlightRoster";
import FlightRoster from "./screens/Commercial Airline/Flight Roster/FlightRoster";
import SpecialOnboarding from "./screens/Commercial Airline/Flight Roster/SpecialOnboarding/SpecialOnboarding";
import ViewHistoricalData from "./screens/Commercial Airline/Historical Data/View Historical Data/ViewHistoricalData";
import InvoiceDetail from "./screens/Commercial Airline/Invoice/InvoiceDetails/InvoiceDetail";
import FeeInvoiceDetails from "./screens/Commercial Airline/Invoice/InvoiceDetails/Tables/FeeInvoiceDetails";
import InvoiceScreen from "./screens/Commercial Airline/Invoice/InvoiceScreen";
import Receipt from "./screens/Commercial Airline/Invoice/Receipt/Receipt";
import ConcessionFeeDetails from "./screens/Commercial Airline/Invoice/Views/ConcessionFeeDetails";
import InvoicingIATAPaymentReciept from "./screens/Commercial Airline/Invoicing IATA/Invoicing IATA Payment Reciept/InvoicingIATAPaymentReciept";
import InvoicingIATAScreen from "./screens/Commercial Airline/Invoicing IATA/InvoicingIATA";
import ViewIATAFlightDetails from "./screens/Commercial Airline/Invoicing IATA/View IATA Flight Details/ViewIATAFlightDetails";
import ViewIATAInvoicing from "./screens/Commercial Airline/Invoicing IATA/View IATA Invoicing/ViewIATAInvoicing";
import NotificationsScreen from "./screens/Commercial Airline/Notifications/NotificationsScreen";
import EscortDetails from "./screens/Commercial Airline/ViewSpecialOnBoarding/EscortApproval/EscortDetails";
import TripDetails from "./screens/Commercial Airline/ViewSpecialOnBoarding/TripDetails/TripDetails";
import ViewSpecialOnboarding from "./screens/Commercial Airline/ViewSpecialOnBoarding/ViewSpecialOnBoarding";
import ROUTE_PATHS from "./utils/routePaths";
import PageNotFound from "./components/element/PageNotFound";
import theme from "./utils/theme";
import { useFormData } from "./helpers/FormDataProvider";
import { StorageContext } from "./services/StorageProvider";
import {
  AA_DEPARTMENT,
  AA_ROLE_NAME,
  ADMIN_ROLE,
  COMMERCIAL_USER_ROLE,
} from "./utils/constants";
import { useLogout } from "./services/LogoutProvider";
import { isRestrictedRouteForRole } from "./utils/utils";

const AppRoutes = () => {
  const location = useLocation();
  const { userData } = useFormData();
  const { userProfileData, aaRolesData } = useContext(StorageContext);
  const { handleUnauthorized, isUnauthorized } = useLogout();

  const realmRoles = userData?.roles || [];
  const aaRoles = aaRolesData?.data || aaRolesData || [];
  const resolvedUserProfile =
    userData?.isAAUser && userProfileData?.data
      ? userProfileData.data
      : userProfileData;

  const aaRoleIdByName = useMemo(() => {
    return aaRoles.reduce((acc, role) => {
      if (role?.name && role?.id) {
        acc[role.name] = role.id;
      }
      return acc;
    }, {});
  }, [aaRoles]);

  const resolvedRole = useMemo(() => {
    const commercialRole = resolvedUserProfile?.role?.toUpperCase();
    if (
      commercialRole === COMMERCIAL_USER_ROLE.AIRLINE_ADMIN ||
      commercialRole === COMMERCIAL_USER_ROLE.AIRLINE_AGENT
    ) {
      return commercialRole;
    }

    const aaDepartment = resolvedUserProfile?.department?.toUpperCase();
    const aaRoleId = resolvedUserProfile?.roleId;

    const isSuperAdmin =
      aaDepartment === AA_DEPARTMENT.ADMIN &&
      aaRoleId === aaRoleIdByName[AA_ROLE_NAME.ADMIN];
    if (isSuperAdmin) {
      return ADMIN_ROLE.SUPER_ADMIN;
    }

    const isUserOperation =
      aaDepartment === AA_DEPARTMENT.OPERATIONS &&
      aaRoleId === aaRoleIdByName[AA_ROLE_NAME.USER_OPERATIONS];
    const isOperationRole =
      aaDepartment === AA_DEPARTMENT.OPERATIONS &&
      aaRoleId === aaRoleIdByName[AA_ROLE_NAME.OPERATIONS];
    const isRevenueOfficer =
      aaDepartment === AA_DEPARTMENT.OPERATIONS &&
      aaRoleId === aaRoleIdByName[AA_ROLE_NAME.REVENUE_OFFICER];
    if (isOperationRole || isUserOperation || isRevenueOfficer) {
      return ADMIN_ROLE.OPERATION;
    }

    if (
      commercialRole?.includes(ADMIN_ROLE.SUPER_ADMIN) ||
      realmRoles.some((role) => role.includes(ADMIN_ROLE.SUPER_ADMIN))
    ) {
      return ADMIN_ROLE.SUPER_ADMIN;
    }

    return null;
  }, [
    aaRoleIdByName,
    realmRoles,
    resolvedUserProfile?.department,
    resolvedUserProfile?.role,
    resolvedUserProfile?.roleId,
  ]);

  const isRestrictedRoute = isRestrictedRouteForRole(
    resolvedRole,
    location.pathname
  );

  const isRevenueOfficer =
    resolvedUserProfile?.department?.toUpperCase() === AA_DEPARTMENT.OPERATIONS &&
    resolvedUserProfile?.roleId === aaRoleIdByName[AA_ROLE_NAME.REVENUE_OFFICER];
  const isOperationUser =
    resolvedUserProfile?.department?.toUpperCase() === AA_DEPARTMENT.OPERATIONS &&
    resolvedUserProfile?.roleId === aaRoleIdByName[AA_ROLE_NAME.USER_OPERATIONS];
  const isBlockedAirlineAppUser = isOperationUser || isRevenueOfficer;

  useEffect(() => {
    if (isBlockedAirlineAppUser && !isUnauthorized) {
      handleUnauthorized();
    }
  }, [
    handleUnauthorized,
    isBlockedAirlineAppUser,
    isUnauthorized,
  ]);

  if (isBlockedAirlineAppUser || isRestrictedRoute) {
    return (
      <Routes>
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path={ROUTE_PATHS.HOME}
        element={<Navigate to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME} replace />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
        element={<CommercialAirlineHome />}
      />
      {
        <Route
          path={ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER}
          element={<FlightRoster />}
        />
      }
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_CREATE_FLIGHT_PLAN}
        element={<CreateFlightPlan />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_CONCESSION_FEE}
        element={<ConcessionFee />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_CONCESSION_FEE_DETAILS}
        element={<ConcessionFeeDetails />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER_EDIT}
        element={<EditFlightRoster />}
      />
      {
        <Route
          path={ROUTE_PATHS.COMMERCIAL_AIRLINE_SPECIAL_ONBOARDING}
          element={<SpecialOnboarding />}
        />
      }
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_ACCOUNT_MAINTENANCE}
        element={<AccountMaintenance />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE}
        element={<InvoiceScreen />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_DETAILS}
        element={<InvoiceDetail />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_RECEIPT}
        element={<Receipt />}
      />
      {
        <Route
          path={ROUTE_PATHS.COMMERCIAL_AIRLINE_HISTORICAL_DATA_VIEW}
          element={<ViewHistoricalData />}
        />
      }
      {
        <Route
          path={ROUTE_PATHS.COMMERCIAL_AIRLINE_VIEW_SPECIAL_ONBOARDING}
          element={<ViewSpecialOnboarding />}
        />
      }
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_VIEW_SPECIAL_ONBOARDING_TRIP_DETAILS}
        element={<TripDetails />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_ESCORT_DETAILS}
        element={<EscortDetails />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE}
        element={<DataSource />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE_AIRLINES}
        element={<AirlineList />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE_AVIATION_STACK_FAILURE}
        element={<AviationStackFailure />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE_AVIATION_STACK_SUCCESS}
        element={<AviationStackSuccess />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_FEE_INVOICE_DETAILS}
        element={<FeeInvoiceDetails />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_NOTIFICATIONS}
        element={<NotificationsScreen />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICING_IATA}
        element={<InvoicingIATAScreen />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICING_IATA_PAYMENT_RECIEPT}
        element={<InvoicingIATAPaymentReciept />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICING_IATA_VIEW}
        element={<ViewIATAInvoicing />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICING_IATA_VIEW_FLIGHT_DETAILS}
        element={<ViewIATAFlightDetails />}
      />
      <Route
        path={ROUTE_PATHS.COMMERCIAL_AIRLINE_PASSENGER_DISPUTE_COUNT_DETAILS}
        element={<DisputeDetailsScreen />}
      />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
};

export default App;
